// DATXALE_EMBEDDED_SIGNUP_BUTTON_20260505
// Standalone React component for Meta WhatsApp Embedded Signup.
// Drop into WhatsAppApiConnections page header to show alongside "NUEVA CONEXIÓN".

import React, { useEffect, useState, useCallback } from "react";
import { Button, CircularProgress, Tooltip } from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// DATXALE_ES_COMINGSOON_FLAG_20260520
// Embedded Signup requiere ser Tech Provider/Solution Partner aprobado por Meta.
// Mientras Datxale no tenga esa aprobación, mostramos el botón como "Próximamente".
// El admin configura cada cliente manualmente via "NUEVA CONEXIÓN".
// Cuando Meta apruebe, cambiar a false y rebuildear.
const ES_COMING_SOON = false;

const FB_SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";

function loadFbSdk(appId, version) {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }
    window.fbAsyncInit = function () {
      window.FB.init({
        appId,
        cookie: true,
        xfbml: false,
        version: version || "v18.0",
      });
      resolve(window.FB);
    };
    if (document.getElementById("facebook-jssdk")) return;
    const s = document.createElement("script");
    s.id = "facebook-jssdk";
    s.src = FB_SDK_SRC;
    s.async = true;
    s.defer = true;
    s.crossOrigin = "anonymous";
    s.onerror = () => reject(new Error("FB SDK failed to load"));
    document.body.appendChild(s);
  });
}

export default function EmbeddedSignupButton({ onConnected }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/whatsapp-api/embedded-signup/config");
        if (!alive) return;
        setConfig(data || null);
      } catch (_) {
        if (alive) setConfig({ ready: false });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Meta Embedded Signup session info (phone, waba) is delivered out-of-band via postMessage
  const [signupSession, setSignupSession] = useState({});
  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") {
        return;
      }
      try {
        const data = JSON.parse(event.data || "{}");
        if (data.type === "WA_EMBEDDED_SIGNUP" && data.event === "FINISH") {
          setSignupSession({
            phoneNumberId: data.data?.phone_number_id,
            whatsappBusinessAccountId: data.data?.waba_id,
            businessId: data.data?.business_id,
          });
        }
      } catch (_) {
        /* ignore non-JSON messages */
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const launch = useCallback(async () => {
    // DATXALE_DEBUG_ES_20260520
    console.log('[ES] launch() called. config=', JSON.stringify(config));
    if (!config?.ready || !config?.appId || !config?.configId) {
      console.warn('[ES] config not ready');
      toast.info(
        "Embedded Signup aún no está activado. El admin de Klorum debe completar el Meta App Review primero."
      );
      return;
    }
    setBusy(true);
    try {
      console.log('[ES] loading FB SDK with appId=', config.appId);
      const FB = await loadFbSdk(config.appId, config.graphApiVersion);
      console.log('[ES] FB SDK loaded OK. Calling FB.login config_id=', config.configId);

      FB.login(
        async (response) => {
          console.log('[ES] FB.login callback. response=', JSON.stringify(response));
          try {
            if (response?.status !== "connected" || !response?.authResponse?.code) {
              setBusy(false);
              if (response?.status === "not_authorized") {
                toast.info("Cancelaste la conexión de WhatsApp.");
              } else {
                toast.error("FB.login status=" + (response?.status || 'undefined') + " (ver consola)", { autoClose: 9000 });
              }
              return;
            }

            const code = response.authResponse.code;
            const session = signupSession || {};
            if (!session.phoneNumberId || !session.whatsappBusinessAccountId) {
              setBusy(false);
              toast.error("No se recibió phone_number_id/waba_id desde Meta. Reintenta.");
              return;
            }

            const { data: created } = await api.post(
              "/whatsapp-api/embedded-signup/callback",
              {
                code,
                phoneNumberId: session.phoneNumberId,
                whatsappBusinessAccountId: session.whatsappBusinessAccountId,
                businessId: session.businessId,
                isDefault: false,
              }
            );

            toast.success(
              `WhatsApp conectado: ${created?.name || created?.phoneNumberId || "conexión creada"}`
            );
            onConnected?.(created);
          } catch (err) {
            toastError(err);
          } finally {
            setBusy(false);
          }
        },
        {
          config_id: config.configId,
          response_type: "code",
          override_default_response_type: true,
          extras: {
            feature: "whatsapp_embedded_signup",
            sessionInfoVersion: 3,
          },
        }
      );
    } catch (err) {
      setBusy(false);
      // DATXALE_DEBUG_ES_20260520
      console.error('[ES] launch() outer catch:', err);
      const msg = err?.message || err?.toString() || 'Unknown ES error';
      toast.error('ES error: ' + msg, { autoClose: 10000 });
    }
  }, [config, signupSession, onConnected]);

  if (loading) {
    return null;
  }

  // DATXALE_ES_COMINGSOON_FLAG_20260520
  // Mostrar botón como "Próximamente" hasta tener aprobación de Meta como Tech Provider.
  if (ES_COMING_SOON) {
    return (
      <Tooltip title="Próximamente: conexión 1-click con Facebook. Mientras tanto, el admin de Klorum configura manualmente cada cliente vía 'NUEVA CONEXIÓN'.">
        <span>
          <Button
            variant="contained"
            disabled={true}
            style={{ marginRight: 8, opacity: 0.65 }}
          >
            Próximamente · Conectar con Facebook
          </Button>
        </span>
      </Tooltip>
    );
  }

  const tooltipText = config?.ready
    ? "Iniciá sesión con Facebook y autorizá tu WhatsApp Business — sin pegar tokens."
    : "Pendiente: el admin Klorum debe completar Meta App Review para habilitar este flujo 1-click.";

  return (
    <Tooltip title={tooltipText}>
      <span>
        <Button
          variant="contained"
          color="primary"
          disabled={!config?.ready || busy}
          onClick={launch}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
          style={{ marginRight: 8 }}
        >
          {busy ? "Conectando..." : "Conectar con Facebook"}
        </Button>
      </span>
    </Tooltip>
  );
}
